/**
 * Studio Theme Presets
 *
 * Each preset defines HSL CSS variable overrides for both light and dark modes.
 * These override the base variables defined in packages/ui/globals.css.
 *
 * Variable format: HSL components without hsl() wrapper, e.g. "249 100% 67%"
 * This matches the existing convention where Tailwind applies hsl() in the config.
 */

export interface ThemePreset {
  key: string;
  name: string;
  description: string;
  /** Preview swatch color (hex) shown in the theme picker grid */
  swatchColor: string;
  /** Secondary swatch color for two-tone preview */
  swatchAccent: string;
  light: ThemeVariables;
  dark: ThemeVariables;
}

interface ThemeVariables {
  "--background": string;
  "--foreground": string;
  "--card": string;
  "--card-foreground": string;
  "--popover": string;
  "--popover-foreground": string;
  "--primary": string;
  "--primary-foreground": string;
  "--secondary": string;
  "--secondary-foreground": string;
  "--muted": string;
  "--muted-foreground": string;
  "--accent": string;
  "--accent-foreground": string;
  "--destructive": string;
  "--destructive-foreground": string;
  "--border": string;
  "--input": string;
  "--ring": string;
}

export const THEME_PRESETS: ThemePreset[] = [
  {
    key: "midnight",
    name: "Midnight",
    description: "Deep navy backgrounds, cool grey text, electric blue accent",
    swatchColor: "#0D1117",
    swatchAccent: "#7B5BFF",
    light: {
      "--background": "0 0% 100%",
      "--foreground": "217 14% 11%",
      "--card": "0 0% 100%",
      "--card-foreground": "217 14% 11%",
      "--popover": "0 0% 100%",
      "--popover-foreground": "217 14% 11%",
      "--primary": "249 100% 67%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "0 0% 97%",
      "--secondary-foreground": "217 14% 11%",
      "--muted": "0 0% 94%",
      "--muted-foreground": "0 0% 45%",
      "--accent": "0 0% 94%",
      "--accent-foreground": "217 14% 11%",
      "--destructive": "0 79% 59%",
      "--destructive-foreground": "0 0% 100%",
      "--border": "0 0% 90%",
      "--input": "0 0% 90%",
      "--ring": "0 0% 90%",
    },
    dark: {
      "--background": "216 28% 7%",
      "--foreground": "0 0% 100%",
      "--card": "216 17% 12%",
      "--card-foreground": "0 0% 100%",
      "--popover": "213 11% 16%",
      "--popover-foreground": "0 0% 100%",
      "--primary": "249 100% 67%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "213 11% 16%",
      "--secondary-foreground": "0 0% 100%",
      "--muted": "213 11% 16%",
      "--muted-foreground": "210 2% 63%",
      "--accent": "210 6% 25%",
      "--accent-foreground": "0 0% 100%",
      "--destructive": "0 79% 59%",
      "--destructive-foreground": "0 0% 100%",
      "--border": "210 6% 25%",
      "--input": "213 11% 16%",
      "--ring": "210 6% 25%",
    },
  },
  {
    key: "ocean",
    name: "Ocean",
    description: "Teal primary, dark slate backgrounds, seafoam accents",
    swatchColor: "#0F172A",
    swatchAccent: "#06B6D4",
    light: {
      "--background": "0 0% 100%",
      "--foreground": "215 25% 12%",
      "--card": "0 0% 100%",
      "--card-foreground": "215 25% 12%",
      "--popover": "0 0% 100%",
      "--popover-foreground": "215 25% 12%",
      "--primary": "187 86% 43%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "195 10% 95%",
      "--secondary-foreground": "215 25% 12%",
      "--muted": "195 10% 93%",
      "--muted-foreground": "215 10% 45%",
      "--accent": "195 10% 93%",
      "--accent-foreground": "215 25% 12%",
      "--destructive": "0 79% 59%",
      "--destructive-foreground": "0 0% 100%",
      "--border": "195 10% 88%",
      "--input": "195 10% 88%",
      "--ring": "195 10% 88%",
    },
    dark: {
      "--background": "222 47% 11%",
      "--foreground": "195 10% 95%",
      "--card": "217 33% 14%",
      "--card-foreground": "195 10% 95%",
      "--popover": "215 28% 17%",
      "--popover-foreground": "195 10% 95%",
      "--primary": "187 86% 43%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "215 28% 17%",
      "--secondary-foreground": "195 10% 95%",
      "--muted": "215 28% 17%",
      "--muted-foreground": "215 15% 55%",
      "--accent": "215 20% 24%",
      "--accent-foreground": "195 10% 95%",
      "--destructive": "0 79% 59%",
      "--destructive-foreground": "0 0% 100%",
      "--border": "215 20% 24%",
      "--input": "215 28% 17%",
      "--ring": "215 20% 24%",
    },
  },
  {
    key: "sunset",
    name: "Sunset",
    description: "Warm amber accent, dark warm-grey backgrounds, cream text",
    swatchColor: "#1C1917",
    swatchAccent: "#F59E0B",
    light: {
      "--background": "40 33% 99%",
      "--foreground": "24 10% 12%",
      "--card": "40 33% 99%",
      "--card-foreground": "24 10% 12%",
      "--popover": "40 33% 99%",
      "--popover-foreground": "24 10% 12%",
      "--primary": "38 92% 50%",
      "--primary-foreground": "24 10% 8%",
      "--secondary": "36 20% 94%",
      "--secondary-foreground": "24 10% 12%",
      "--muted": "36 20% 92%",
      "--muted-foreground": "24 6% 45%",
      "--accent": "36 20% 92%",
      "--accent-foreground": "24 10% 12%",
      "--destructive": "0 79% 59%",
      "--destructive-foreground": "0 0% 100%",
      "--border": "36 15% 87%",
      "--input": "36 15% 87%",
      "--ring": "36 15% 87%",
    },
    dark: {
      "--background": "24 10% 10%",
      "--foreground": "36 20% 92%",
      "--card": "24 8% 14%",
      "--card-foreground": "36 20% 92%",
      "--popover": "24 7% 17%",
      "--popover-foreground": "36 20% 92%",
      "--primary": "38 92% 50%",
      "--primary-foreground": "24 10% 8%",
      "--secondary": "24 7% 17%",
      "--secondary-foreground": "36 20% 92%",
      "--muted": "24 7% 17%",
      "--muted-foreground": "24 5% 55%",
      "--accent": "24 6% 24%",
      "--accent-foreground": "36 20% 92%",
      "--destructive": "0 79% 59%",
      "--destructive-foreground": "0 0% 100%",
      "--border": "24 6% 24%",
      "--input": "24 7% 17%",
      "--ring": "24 6% 24%",
    },
  },
  {
    key: "forest",
    name: "Forest",
    description: "Deep green accent, olive tones, warm off-white text",
    swatchColor: "#14201A",
    swatchAccent: "#22C55E",
    light: {
      "--background": "0 0% 100%",
      "--foreground": "150 10% 12%",
      "--card": "0 0% 100%",
      "--card-foreground": "150 10% 12%",
      "--popover": "0 0% 100%",
      "--popover-foreground": "150 10% 12%",
      "--primary": "142 71% 45%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "140 12% 94%",
      "--secondary-foreground": "150 10% 12%",
      "--muted": "140 12% 92%",
      "--muted-foreground": "150 6% 45%",
      "--accent": "140 12% 92%",
      "--accent-foreground": "150 10% 12%",
      "--destructive": "0 79% 59%",
      "--destructive-foreground": "0 0% 100%",
      "--border": "140 8% 87%",
      "--input": "140 8% 87%",
      "--ring": "140 8% 87%",
    },
    dark: {
      "--background": "155 25% 8%",
      "--foreground": "140 12% 92%",
      "--card": "152 18% 12%",
      "--card-foreground": "140 12% 92%",
      "--popover": "150 14% 15%",
      "--popover-foreground": "140 12% 92%",
      "--primary": "142 71% 45%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "150 14% 15%",
      "--secondary-foreground": "140 12% 92%",
      "--muted": "150 14% 15%",
      "--muted-foreground": "150 8% 50%",
      "--accent": "150 10% 22%",
      "--accent-foreground": "140 12% 92%",
      "--destructive": "0 79% 59%",
      "--destructive-foreground": "0 0% 100%",
      "--border": "150 10% 22%",
      "--input": "150 14% 15%",
      "--ring": "150 10% 22%",
    },
  },
  {
    key: "slate",
    name: "Slate",
    description: "Neutral grey palette, minimal colour, subtle blue-grey accent",
    swatchColor: "#1E293B",
    swatchAccent: "#64748B",
    light: {
      "--background": "0 0% 100%",
      "--foreground": "215 20% 15%",
      "--card": "0 0% 100%",
      "--card-foreground": "215 20% 15%",
      "--popover": "0 0% 100%",
      "--popover-foreground": "215 20% 15%",
      "--primary": "215 16% 47%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "210 10% 96%",
      "--secondary-foreground": "215 20% 15%",
      "--muted": "210 10% 94%",
      "--muted-foreground": "215 10% 45%",
      "--accent": "210 10% 94%",
      "--accent-foreground": "215 20% 15%",
      "--destructive": "0 79% 59%",
      "--destructive-foreground": "0 0% 100%",
      "--border": "210 10% 89%",
      "--input": "210 10% 89%",
      "--ring": "210 10% 89%",
    },
    dark: {
      "--background": "217 33% 14%",
      "--foreground": "210 10% 94%",
      "--card": "215 28% 17%",
      "--card-foreground": "210 10% 94%",
      "--popover": "215 22% 20%",
      "--popover-foreground": "210 10% 94%",
      "--primary": "215 20% 55%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "215 22% 20%",
      "--secondary-foreground": "210 10% 94%",
      "--muted": "215 22% 20%",
      "--muted-foreground": "215 12% 50%",
      "--accent": "215 16% 28%",
      "--accent-foreground": "210 10% 94%",
      "--destructive": "0 79% 59%",
      "--destructive-foreground": "0 0% 100%",
      "--border": "215 16% 28%",
      "--input": "215 22% 20%",
      "--ring": "215 16% 28%",
    },
  },
  {
    key: "berry",
    name: "Berry",
    description: "Deep plum/magenta accent, dark purple-grey backgrounds",
    swatchColor: "#1A1025",
    swatchAccent: "#D946EF",
    light: {
      "--background": "0 0% 100%",
      "--foreground": "280 10% 12%",
      "--card": "0 0% 100%",
      "--card-foreground": "280 10% 12%",
      "--popover": "0 0% 100%",
      "--popover-foreground": "280 10% 12%",
      "--primary": "292 84% 61%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "285 12% 95%",
      "--secondary-foreground": "280 10% 12%",
      "--muted": "285 12% 93%",
      "--muted-foreground": "280 6% 45%",
      "--accent": "285 12% 93%",
      "--accent-foreground": "280 10% 12%",
      "--destructive": "0 79% 59%",
      "--destructive-foreground": "0 0% 100%",
      "--border": "285 8% 88%",
      "--input": "285 8% 88%",
      "--ring": "285 8% 88%",
    },
    dark: {
      "--background": "275 30% 8%",
      "--foreground": "285 12% 93%",
      "--card": "278 22% 12%",
      "--card-foreground": "285 12% 93%",
      "--popover": "276 18% 16%",
      "--popover-foreground": "285 12% 93%",
      "--primary": "292 84% 61%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "276 18% 16%",
      "--secondary-foreground": "285 12% 93%",
      "--muted": "276 18% 16%",
      "--muted-foreground": "280 8% 50%",
      "--accent": "278 14% 24%",
      "--accent-foreground": "285 12% 93%",
      "--destructive": "0 79% 59%",
      "--destructive-foreground": "0 0% 100%",
      "--border": "278 14% 24%",
      "--input": "276 18% 16%",
      "--ring": "278 14% 24%",
    },
  },
  {
    key: "ember",
    name: "Ember",
    description: "Deep red/crimson accent, charcoal backgrounds, warm highlights",
    swatchColor: "#1C1210",
    swatchAccent: "#EF4444",
    light: {
      "--background": "0 0% 100%",
      "--foreground": "0 10% 12%",
      "--card": "0 0% 100%",
      "--card-foreground": "0 10% 12%",
      "--popover": "0 0% 100%",
      "--popover-foreground": "0 10% 12%",
      "--primary": "0 84% 60%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "0 10% 95%",
      "--secondary-foreground": "0 10% 12%",
      "--muted": "0 10% 93%",
      "--muted-foreground": "0 5% 45%",
      "--accent": "0 10% 93%",
      "--accent-foreground": "0 10% 12%",
      "--destructive": "0 84% 60%",
      "--destructive-foreground": "0 0% 100%",
      "--border": "0 8% 88%",
      "--input": "0 8% 88%",
      "--ring": "0 8% 88%",
    },
    dark: {
      "--background": "0 15% 7%",
      "--foreground": "0 10% 93%",
      "--card": "0 10% 11%",
      "--card-foreground": "0 10% 93%",
      "--popover": "0 8% 15%",
      "--popover-foreground": "0 10% 93%",
      "--primary": "0 84% 60%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "0 8% 15%",
      "--secondary-foreground": "0 10% 93%",
      "--muted": "0 8% 15%",
      "--muted-foreground": "0 5% 50%",
      "--accent": "0 7% 22%",
      "--accent-foreground": "0 10% 93%",
      "--destructive": "0 84% 60%",
      "--destructive-foreground": "0 0% 100%",
      "--border": "0 7% 22%",
      "--input": "0 8% 15%",
      "--ring": "0 7% 22%",
    },
  },
  {
    key: "arctic",
    name: "Arctic",
    description: "Icy blue-white backgrounds, steel blue accents, high contrast",
    swatchColor: "#F0F9FF",
    swatchAccent: "#3B82F6",
    light: {
      "--background": "204 100% 97%",
      "--foreground": "214 25% 12%",
      "--card": "0 0% 100%",
      "--card-foreground": "214 25% 12%",
      "--popover": "0 0% 100%",
      "--popover-foreground": "214 25% 12%",
      "--primary": "217 91% 60%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "210 20% 94%",
      "--secondary-foreground": "214 25% 12%",
      "--muted": "210 20% 92%",
      "--muted-foreground": "214 10% 42%",
      "--accent": "210 20% 92%",
      "--accent-foreground": "214 25% 12%",
      "--destructive": "0 79% 59%",
      "--destructive-foreground": "0 0% 100%",
      "--border": "210 15% 86%",
      "--input": "210 15% 86%",
      "--ring": "210 15% 86%",
    },
    dark: {
      "--background": "216 28% 9%",
      "--foreground": "210 20% 94%",
      "--card": "215 25% 13%",
      "--card-foreground": "210 20% 94%",
      "--popover": "215 20% 16%",
      "--popover-foreground": "210 20% 94%",
      "--primary": "217 91% 60%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "215 20% 16%",
      "--secondary-foreground": "210 20% 94%",
      "--muted": "215 20% 16%",
      "--muted-foreground": "215 12% 52%",
      "--accent": "215 16% 24%",
      "--accent-foreground": "210 20% 94%",
      "--destructive": "0 79% 59%",
      "--destructive-foreground": "0 0% 100%",
      "--border": "215 16% 24%",
      "--input": "215 20% 16%",
      "--ring": "215 16% 24%",
    },
  },
  {
    key: "sand",
    name: "Sand",
    description: "Warm beige backgrounds, terracotta accent, dark brown text",
    swatchColor: "#FAF7F2",
    swatchAccent: "#C2410C",
    light: {
      "--background": "40 30% 97%",
      "--foreground": "24 15% 15%",
      "--card": "40 25% 99%",
      "--card-foreground": "24 15% 15%",
      "--popover": "40 25% 99%",
      "--popover-foreground": "24 15% 15%",
      "--primary": "20 80% 40%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "36 18% 93%",
      "--secondary-foreground": "24 15% 15%",
      "--muted": "36 18% 91%",
      "--muted-foreground": "24 8% 45%",
      "--accent": "36 18% 91%",
      "--accent-foreground": "24 15% 15%",
      "--destructive": "0 79% 59%",
      "--destructive-foreground": "0 0% 100%",
      "--border": "36 14% 85%",
      "--input": "36 14% 85%",
      "--ring": "36 14% 85%",
    },
    dark: {
      "--background": "24 12% 9%",
      "--foreground": "36 18% 91%",
      "--card": "24 10% 13%",
      "--card-foreground": "36 18% 91%",
      "--popover": "24 8% 16%",
      "--popover-foreground": "36 18% 91%",
      "--primary": "20 80% 50%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "24 8% 16%",
      "--secondary-foreground": "36 18% 91%",
      "--muted": "24 8% 16%",
      "--muted-foreground": "24 6% 50%",
      "--accent": "24 7% 23%",
      "--accent-foreground": "36 18% 91%",
      "--destructive": "0 79% 59%",
      "--destructive-foreground": "0 0% 100%",
      "--border": "24 7% 23%",
      "--input": "24 8% 16%",
      "--ring": "24 7% 23%",
    },
  },
  {
    key: "monochrome",
    name: "Monochrome",
    description: "Pure black, white, and greys only. No colour accent",
    swatchColor: "#000000",
    swatchAccent: "#737373",
    light: {
      "--background": "0 0% 100%",
      "--foreground": "0 0% 9%",
      "--card": "0 0% 100%",
      "--card-foreground": "0 0% 9%",
      "--popover": "0 0% 100%",
      "--popover-foreground": "0 0% 9%",
      "--primary": "0 0% 9%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "0 0% 96%",
      "--secondary-foreground": "0 0% 9%",
      "--muted": "0 0% 94%",
      "--muted-foreground": "0 0% 45%",
      "--accent": "0 0% 94%",
      "--accent-foreground": "0 0% 9%",
      "--destructive": "0 79% 59%",
      "--destructive-foreground": "0 0% 100%",
      "--border": "0 0% 89%",
      "--input": "0 0% 89%",
      "--ring": "0 0% 89%",
    },
    dark: {
      "--background": "0 0% 4%",
      "--foreground": "0 0% 95%",
      "--card": "0 0% 8%",
      "--card-foreground": "0 0% 95%",
      "--popover": "0 0% 12%",
      "--popover-foreground": "0 0% 95%",
      "--primary": "0 0% 95%",
      "--primary-foreground": "0 0% 4%",
      "--secondary": "0 0% 12%",
      "--secondary-foreground": "0 0% 95%",
      "--muted": "0 0% 12%",
      "--muted-foreground": "0 0% 50%",
      "--accent": "0 0% 20%",
      "--accent-foreground": "0 0% 95%",
      "--destructive": "0 79% 59%",
      "--destructive-foreground": "0 0% 100%",
      "--border": "0 0% 20%",
      "--input": "0 0% 12%",
      "--ring": "0 0% 20%",
    },
  },
];

export const THEME_PRESET_MAP = Object.fromEntries(
  THEME_PRESETS.map((preset) => [preset.key, preset]),
) as Record<string, ThemePreset>;

export const DEFAULT_THEME_KEY = "midnight";
